import { useEffect, useState } from 'react';
import { Loader2, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import useAnalyticsStore from '../stores/analyticsStore';

// Time-bucketed event histogram ([6.4]). Tracks the SAME filters as the list:
// any filter change (or bucket toggle) refetches the aggregate. Clicking a bar
// narrows the list (and drills day -> hour) to that bucket's window.

// datetime-local input value (YYYY-MM-DDTHH:mm) in LOCAL time, so a click-set
// from/to renders correctly in the filter panel's datetime-local inputs.
function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function formatTick(iso, bucket) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const opts = bucket === 'hour'
    ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
    : { month: 'short', day: 'numeric' };
  return d.toLocaleString(undefined, opts);
}

function formatFullLabel(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

function HistTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const { bucketStart, count } = payload[0].payload;
  return (
    <div className="analytics-hist-tooltip">
      <div className="analytics-hist-tooltip-label">{formatFullLabel(bucketStart)}</div>
      <div className="analytics-hist-tooltip-count">
        {count} event{count === 1 ? '' : 's'}
      </div>
    </div>
  );
}

const AnalyticsHistogram = () => {
  const filters = useAnalyticsStore((s) => s.filters);
  const aggregate = useAnalyticsStore((s) => s.aggregate);
  const aggLoading = useAnalyticsStore((s) => s.aggLoading);
  const aggError = useAnalyticsStore((s) => s.aggError);
  const fetchAggregate = useAnalyticsStore((s) => s.fetchAggregate);
  const setTimeWindow = useAnalyticsStore((s) => s.setTimeWindow);

  const [bucket, setBucket] = useState('day');

  // Always track the active filters; refetch on filter or bucket change.
  useEffect(() => {
    fetchAggregate(bucket);
  }, [filters, bucket, fetchAggregate]);

  // Click a bar -> narrow the list to that bucket's window; drill day -> hour.
  // bucketStart is the UTC instant of the LOCAL bucket boundary, so deriving the
  // local 'YYYY-MM-DDTHH:mm' strings here and letting buildQuery convert them
  // back to instants keeps the window aligned with the bars. The +unit shortcut
  // is off by an hour at DST edges (acceptable for now).
  const handleBarClick = (data) => {
    const iso = data?.bucketStart ?? data?.payload?.bucketStart;
    if (!iso) return;
    const start = new Date(iso);
    if (Number.isNaN(start.getTime())) return;
    const unitMs = bucket === 'hour' ? 3600e3 : 86400e3;
    const end = new Date(start.getTime() + unitMs);
    // One atomic window write -> one fetchEvents (no from-only intermediate read
    // to win the race). The setBucket drill is local state; the histogram's
    // useEffect([filters, bucket]) still coalesces into one fetchAggregate.
    setTimeWindow(toLocalInputValue(start), toLocalInputValue(end));
    if (bucket === 'day') setBucket('hour');
  };

  const isEmpty = !aggLoading && !aggError && aggregate.length === 0;

  return (
    <div className="analytics-hist">
      <div className="analytics-hist-head">
        <div className="analytics-hist-title">
          <BarChart3 size={16} />
          <span>Events over time</span>
        </div>
        <div className="analytics-hist-toggle">
          <button
            type="button"
            className={'analytics-hist-toggle-btn' + (bucket === 'hour' ? ' active' : '')}
            onClick={() => setBucket('hour')}
          >
            Hour
          </button>
          <button
            type="button"
            className={'analytics-hist-toggle-btn' + (bucket === 'day' ? ' active' : '')}
            onClick={() => setBucket('day')}
          >
            Day
          </button>
        </div>
      </div>

      {aggError && (
        <div className="analytics-hist-state analytics-hist-error">
          Failed to load histogram: {aggError}
        </div>
      )}

      {!aggError && isEmpty && (
        <div className="analytics-hist-state">No events in this range.</div>
      )}

      {!aggError && !isEmpty && (
        <div className="analytics-hist-chart">
          {aggLoading && (
            <div className="analytics-hist-spinner">
              <Loader2 size={18} className="analytics-spin" />
            </div>
          )}
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={aggregate} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="bucketStart"
                tickFormatter={(v) => formatTick(v, bucket)}
                tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }}
                minTickGap={24}
              />
              <YAxis
                allowDecimals={false}
                width={36}
                tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }}
              />
              <Tooltip content={<HistTooltip />} cursor={{ fill: 'var(--accent-alpha-08)' }} />
              <Bar
                dataKey="count"
                fill="var(--accent)"
                radius={[3, 3, 0, 0]}
                onClick={handleBarClick}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default AnalyticsHistogram;
